import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Radio, Zap, CheckCircle2, AlertTriangle, Clock, CircleDot } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const STATES = ['IL', 'MS', 'TN'];
const WINDOWS = ['midday', 'evening'];

const statusBadge = (status) => {
    const map = {
        settled:          { bg: '#39FF14', fg: '#062d00', label: 'SETTLED' },
        already_settled:  { bg: '#00BFFF', fg: '#002a3a', label: 'ALREADY' },
        stale:            { bg: '#FF8C00', fg: '#2a1500', label: 'WAITING' },
        not_found:        { bg: '#A0AAB2', fg: '#151515', label: 'N/A' },
        no_numbers:       { bg: '#A0AAB2', fg: '#151515', label: 'N/A' },
        api_error:        { bg: '#FF3B30', fg: '#fff',    label: 'ERROR' },
    };
    const m = map[status] || { bg: '#4a4a4a', fg: '#fff', label: (status || '—').toUpperCase() };
    return (
        <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-black tracking-wide"
              style={{ background: m.bg, color: m.fg }}>{m.label}</span>
    );
};

const timeAgo = (iso) => {
    if (!iso) return '—';
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
};

const LotteryFeedPanel = () => {
    const [status, setStatus] = useState(null);
    const [previews, setPreviews] = useState({});          // { IL: { preview:[...] }, ... }
    const [loading, setLoading] = useState(false);
    const [busyKey, setBusyKey] = useState(null);
    const [alerts, setAlerts] = useState([]);

    const fetchStatus = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/lottery/admin/feed/status`);
            setStatus(data);
        } catch (e) { /* ignore */ }
    }, []);

    const fetchAlerts = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/lottery/admin/feed/alerts`);
            setAlerts(data.alerts || []);
        } catch (e) { /* ignore */ }
    }, []);

    const fetchPreviews = useCallback(async () => {
        setLoading(true);
        const next = {};
        for (const s of STATES) {
            try {
                const { data } = await axios.get(`${API}/lottery/admin/feed/preview?state=${s}`);
                next[s] = data;
            } catch (e) {
                next[s] = { error: e.response?.data?.detail || e.message };
            }
            // small delay to respect 1-req/sec RapidAPI limit
            await new Promise(r => setTimeout(r, 1200));
        }
        setPreviews(next);
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchStatus();
        fetchAlerts();
        const id = setInterval(fetchAlerts, 60_000); // re-poll alerts every minute
        return () => clearInterval(id);
    }, [fetchStatus, fetchAlerts]);

    const runWindow = async (drawing_time) => {
        const key = `ALL-${drawing_time}`;
        if (busyKey) return;
        setBusyKey(key);
        try {
            const { data } = await axios.post(`${API}/lottery/admin/feed/run?drawing_time=${drawing_time}`);
            const settled = (data.results || []).reduce((acc, st) => {
                return acc + (st.games || []).filter(g => g.status === 'settled').length;
            }, 0);
            toast.success(`${drawing_time} feed ran — ${settled} new draw(s) settled`);
            await fetchStatus();
            await fetchAlerts();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Feed run failed');
        } finally {
            setBusyKey(null);
        }
    };

    const runOne = async (state, drawing_time) => {
        const key = `${state}-${drawing_time}`;
        if (busyKey) return;
        setBusyKey(key);
        try {
            const { data } = await axios.post(`${API}/lottery/admin/feed/run-state?state=${state}&drawing_time=${drawing_time}`);
            const g = (data.games || []);
            const settled = g.filter(x => x.status === 'settled').length;
            const stale = g.filter(x => x.status === 'stale').length;
            const already = g.filter(x => x.status === 'already_settled').length;
            if (settled > 0) toast.success(`${state} ${drawing_time}: ${settled} draw(s) settled`);
            else if (already === g.length) toast.info(`${state} ${drawing_time}: already settled earlier`);
            else if (stale > 0) toast.info(`${state} ${drawing_time}: result not posted yet — try again later`);
            else toast.message(`${state} ${drawing_time}: no action taken`);
            await fetchStatus();
            await fetchAlerts();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Run failed');
        } finally {
            setBusyKey(null);
        }
    };

    const runAllOverdue = async () => {
        if (busyKey || alerts.length === 0) return;
        setBusyKey('ALL-OVERDUE');
        let settled = 0, stale = 0;
        const failed = [];
        try {
            for (let i = 0; i < alerts.length; i++) {
                const { state, drawing_time } = alerts[i];
                if (i > 0) await new Promise(r => setTimeout(r, 1500));  // RapidAPI per-second limit
                try {
                    const { data } = await axios.post(
                        `${API}/lottery/admin/feed/run-state?state=${state}&drawing_time=${drawing_time}`
                    );
                    const g = data.games || [];
                    settled += g.filter(x => x.status === 'settled').length;
                    stale   += g.filter(x => x.status === 'stale').length;
                } catch (_e) {
                    failed.push(`${state} ${drawing_time}`);
                }
            }
            const parts = [];
            if (settled) parts.push(`${settled} draw(s) settled`);
            if (stale) parts.push(`${stale} still stale`);
            if (failed.length) parts.push(`failed: ${failed.join(', ')}`);
            const msg = parts.join(' · ') || 'No action taken';
            if (settled > 0) toast.success(`Run All Overdue: ${msg}`);
            else if (failed.length) toast.error(`Run All Overdue: ${msg}`);
            else toast.info(`Run All Overdue: ${msg}`);
            await fetchStatus();
            await fetchAlerts();
        } finally {
            setBusyKey(null);
        }
    };

    const st = status?.status || {};
    const configured = status?.rapidapi_configured;
    const logs = status?.recent_logs || [];

    return (
        <div className="card-dark p-4" data-testid="lottery-feed-panel" style={{ borderLeft: '3px solid #00BFFF' }}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <Radio className={`w-4 h-4 ${configured ? 'text-[#39FF14] animate-pulse' : 'text-[#FF3B30]'}`} />
                    Auto-Fetch Lottery Feed (RapidAPI)
                    {!configured && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40">
                            NOT CONFIGURED
                        </span>
                    )}
                </h3>
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={fetchStatus}
                        className="border-white/20 text-white h-7 text-[11px]"
                        data-testid="feed-refresh-status">
                        <RefreshCw className="w-3 h-3 mr-1" />Refresh Status
                    </Button>
                    <Button size="sm" variant="outline" onClick={fetchPreviews} disabled={loading}
                        className="border-[#00BFFF]/40 text-[#00BFFF] h-7 text-[11px]"
                        data-testid="feed-preview-all">
                        <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                        {loading ? 'Loading...' : 'Preview API'}
                    </Button>
                </div>
            </div>

            <p className="text-[11px] text-[#A0AAB2] mb-3 leading-snug">
                Runs automatically in Central Time: <b className="text-white">midday window 1:00–3:30 PM</b> (TN/IL/MS)
                and <b className="text-white">evening window 9:30–11:30 PM</b>. Fetches every 10 min until all 6 draws settle.
                You can manually trigger or inspect results below.
            </p>

            {/* Overdue draw alerts (fires 1h past cutoff) */}
            {alerts.length > 0 && (
                <div
                    className="mb-4 rounded-lg p-3 animate-pulse"
                    style={{
                        background: 'linear-gradient(90deg, rgba(255,59,48,0.18) 0%, rgba(255,59,48,0.06) 100%)',
                        border: '1px solid #FF3B30',
                        boxShadow: '0 0 16px rgba(255,59,48,0.25)',
                    }}
                    data-testid="feed-overdue-alerts"
                >
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-[#FF3B30]" />
                        <span className="text-[12px] font-black text-[#FF3B30] tracking-wide uppercase">
                            {alerts.length} Draw{alerts.length > 1 ? 's' : ''} Overdue — Action Needed
                        </span>
                        <Button
                            size="sm"
                            onClick={runAllOverdue}
                            disabled={busyKey === 'ALL-OVERDUE'}
                            className="ml-auto h-7 px-3 text-[10px] font-bold bg-[#FF3B30] text-white hover:bg-[#ff5b52]"
                            data-testid="feed-run-all-overdue"
                            title="Fires manual feed/run-state for every overdue state/drawing in sequence"
                        >
                            <Zap className="w-3 h-3 mr-1" />
                            {busyKey === 'ALL-OVERDUE' ? 'Running…' : 'Run All Overdue'}
                        </Button>
                    </div>
                    <p className="text-[10px] text-[#FFB3B0] mb-2">
                        The following state/drawing windows are 1+ hour past their cutoff with no settled numbers for today.
                        Click the state's "Run" button to manually trigger the fetch.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {alerts.map((a, i) => (
                            <span
                                key={i}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold"
                                style={{ background: 'rgba(255,59,48,0.2)', color: '#FFD6D3', border: '1px solid rgba(255,59,48,0.5)' }}
                                data-testid={`feed-alert-${a.state}-${a.drawing_time}`}
                            >
                                {a.state} {a.drawing_time.toUpperCase()}
                                <span className="opacity-70">
                                    · missing P{a.missing_picks.join('/P')} · +{a.overdue_minutes}m past {a.cutoff}
                                </span>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Last-run summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                {WINDOWS.map(w => (
                    <div key={w} className="rounded-lg p-3"
                         style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                                <CircleDot className="w-3.5 h-3.5" style={{ color: w === 'midday' ? '#FFD700' : '#A855F7' }} />
                                <span className="text-[12px] font-bold text-white uppercase">{w}</span>
                            </div>
                            <span className="text-[10px] text-[#A0AAB2]">
                                {timeAgo(st[`last_run_${w}`])}
                            </span>
                        </div>
                        <Button size="sm" onClick={() => runWindow(w)} disabled={!configured || busyKey === `ALL-${w}`}
                            className="w-full h-7 text-[10px] bg-[#FFD700] text-black hover:bg-[#FFE55C]"
                            data-testid={`feed-run-${w}`}>
                            <Zap className="w-3 h-3 mr-1" />
                            {busyKey === `ALL-${w}` ? 'Running...' : `Run ${w} feed now (all 3 states)`}
                        </Button>
                    </div>
                ))}
            </div>

            {/* Per state × window grid */}
            <div className="overflow-x-auto rounded-lg border border-white/5 mb-3">
                <table className="w-full text-xs">
                    <thead className="bg-[#0a0a0a] text-[#A0AAB2]">
                        <tr>
                            <th className="text-left p-2">State</th>
                            {WINDOWS.map(w => (
                                <th key={w} className="text-left p-2 capitalize">{w} — Pick 3 / Pick 4</th>
                            ))}
                            <th className="text-right p-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {STATES.map(s => {
                            const prev = previews[s];
                            const map = {};
                            (prev?.preview || []).forEach(p => {
                                map[`${p.drawing_time}-${p.pick_type}`] = p;
                            });
                            return (
                                <tr key={s} className="border-t border-white/5 hover:bg-white/5"
                                    data-testid={`feed-state-row-${s}`}>
                                    <td className="p-2 font-black text-white">{s}</td>
                                    {WINDOWS.map(w => {
                                        const p3 = map[`${w}-3`];
                                        const p4 = map[`${w}-4`];
                                        return (
                                            <td key={w} className="p-2">
                                                <CellPreview label="P3" entry={p3} />
                                                <CellPreview label="P4" entry={p4} />
                                            </td>
                                        );
                                    })}
                                    <td className="p-2 text-right space-y-1">
                                        {WINDOWS.map(w => (
                                            <Button key={w} size="sm" variant="outline"
                                                onClick={() => runOne(s, w)}
                                                disabled={!configured || busyKey === `${s}-${w}`}
                                                className="h-6 px-2 text-[10px] border-white/20 text-white w-full"
                                                data-testid={`feed-run-${s}-${w}`}>
                                                {busyKey === `${s}-${w}` ? '…' : `Run ${w}`}
                                            </Button>
                                        ))}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Recent logs */}
            <details className="text-[11px]">
                <summary className="cursor-pointer text-[#A0AAB2] hover:text-white" data-testid="feed-logs-toggle">
                    Recent feed activity ({logs.length})
                </summary>
                <div className="mt-2 space-y-1 max-h-56 overflow-y-auto">
                    {logs.length === 0 && <p className="text-[#666]">No feed activity yet.</p>}
                    {logs.map((l, i) => (
                        <div key={i} className="flex items-start gap-2 p-1.5 rounded bg-white/[0.02]">
                            <LogIcon status={l.status} />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-white font-bold">{l.state}</span>
                                    <span className="text-[#A0AAB2]">Pick {l.pick_type} · {l.drawing_time}</span>
                                    {statusBadge(l.status)}
                                </div>
                                {l.detail && <p className="text-[#A0AAB2] text-[10px] mt-0.5 break-all">{l.detail}</p>}
                            </div>
                            <span className="text-[9px] text-[#666] shrink-0">{timeAgo(l.at)}</span>
                        </div>
                    ))}
                </div>
            </details>
        </div>
    );
};

const CellPreview = ({ label, entry }) => {
    if (!entry) {
        return (
            <div className="flex items-center gap-1.5 text-[#666] text-[10px] py-0.5">
                <span className="font-bold w-6">{label}</span>
                <span>— click Preview API —</span>
            </div>
        );
    }
    if (!entry.found || !entry.winning_numbers) {
        return (
            <div className="flex items-center gap-1.5 text-[10px] py-0.5">
                <span className="font-bold w-6 text-[#666]">{label}</span>
                <span className="text-[#666]">no data</span>
            </div>
        );
    }
    return (
        <div className="flex items-center gap-1.5 py-0.5">
            <span className="font-bold w-6 text-[#A0AAB2] text-[10px]">{label}</span>
            <div className="flex gap-0.5">
                {entry.winning_numbers.map((n, i) => (
                    <span key={i} className="inline-block w-4 h-4 text-[9px] font-black bg-[#FFD700]/15 text-[#FFD700] rounded text-center leading-4">
                        {n}
                    </span>
                ))}
            </div>
            <span className="text-[9px] text-[#666] ml-1">{entry.draw_date}</span>
        </div>
    );
};

const LogIcon = ({ status }) => {
    if (status === 'settled') return <CheckCircle2 className="w-3.5 h-3.5 text-[#39FF14] mt-0.5 shrink-0" />;
    if (status === 'already_settled') return <CheckCircle2 className="w-3.5 h-3.5 text-[#00BFFF] mt-0.5 shrink-0" />;
    if (status === 'stale') return <Clock className="w-3.5 h-3.5 text-[#FF8C00] mt-0.5 shrink-0" />;
    if (status === 'api_error') return <AlertTriangle className="w-3.5 h-3.5 text-[#FF3B30] mt-0.5 shrink-0" />;
    return <CircleDot className="w-3.5 h-3.5 text-[#A0AAB2] mt-0.5 shrink-0" />;
};

export default LotteryFeedPanel;
